import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIsBestInDentalVisible } from '~/domain/BestInDental';
import { shouldUseDsoStructure } from '~/growthbook';
import { usePaymentExpiration } from '~/hooks/usePaymentExpiration';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { fireMenuBarHelpClicked } from '~/posthogs/events';
import { openPlatformFeedbackSurvey } from '~/posthogs/surveys';
import {
  fetchNotificationSettings,
  HIDE_LINK_REFERRAL_ACCOUNT_POPUP,
  LINK_REFERRAL_ACCOUNT,
  linkReferralAccount,
  logOut,
  LOGOUT_ACTION,
} from '../../actions';
import { createLoadingSelector } from '../../api/selectors';
import { Column, Screen } from '@tempmee/react';
import { SolidExclamationWarningAlertIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Row, CenteredSpinner } from '../../commonComponents';
import { useIsReferralDisabledForState, useLoading } from '../../hooks';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useTrackEvent } from '../../hooks/useTrackEvent';
import { POSTHOG_EVENTS } from '~/services/posthog';
import '../../themes/global.scss';
import { Links } from '../../constants';
import { RemoteConfigUtils } from '../../utils/RemoteConfigUtils';
import { useAccountInformation } from '../AccountInformation/hooks/useAccountInformation/useAccountInformation';
import { useNotificationTypeMap } from '../NotificationSettings/hooks/useNotificationSettings';
import { LinkReferralPopup } from '../Referral/components/LinkReferralPopup';
import ConfirmPopup from '../shared/ConfirmPopup';
import { AccountMenuItem } from './components/AccountMenuItem';
import { BestInDentalWinner } from './components/BestInDentalWinner';
import ProfileHeaderCard from './ProfileHeaderCard';

export default function Profile() {
  const dispatch = useDispatch();
  const trackEvent = useTrackEvent();
  const allowedWcStates = RemoteConfigUtils.getAllowedWcStates();
  const loadingSelector = createLoadingSelector([LOGOUT_ACTION]);
  const [showLogoutPopup, setShowLogoutPopup] = useState();
  const { shouldShowWarning } = useNotificationTypeMap();
  const childOffices = useSelector((state) => state.user.childOffices);
  const user = useUserSelector();
  const notificationSettings = useSelector((state) => state.user.notificationSettings);
  const showNotificationWarning = notificationSettings?.some(({ event, ...settings }) =>
    shouldShowWarning(event, settings),
  );
  const { isValid: isAccountInformationValid } = useAccountInformation();
  const { isPaymentExpired, isPaymentExpiring } = usePaymentExpiration();
  const selectedOfficeId = useSelector((state) => state.user.selectedOfficeId);

  const isLoading = useSelector((state) => loadingSelector(state));

  const shouldBeDisplayedForParent = childOffices?.find((office) =>
    allowedWcStates?.includes(office.address?.[0]?.state),
  );

  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();
  const { visible: isBestInDentalVisible } = useIsBestInDentalVisible();

  const { isLoading: isLinkingReferral } = useLoading([LINK_REFERRAL_ACCOUNT]);
  const { showLinkReferralAccountPopup, linkReferralError } = useAppSelector((state) => state.user);
  const referralLinked = useAppSelector(
    (state) => state.user?.user?.referralId || state.user?.selectedChildOffice?.referralId,
  );
  const referralScreenDisabled = useIsReferralDisabledForState();
  const userCompletedShifts = user?.completedShiftCount && user?.completedShiftCount > 0;
  const showReferralLinkMenu = !referralLinked && !referralScreenDisabled && !userCompletedShifts;

  const showOversightMenu = shouldUseDsoStructure() && !selectedOfficeId;
  const showOfficeMenu = shouldUseDsoStructure() && selectedOfficeId;
  const showDefaultMenu =
    !shouldUseDsoStructure() ||
    (shouldUseDsoStructure() && selectedOfficeId && !childOffices?.length);

  const menuVisibility = {
    profileHeaderCard: showOfficeMenu || showDefaultMenu,
    accountInformation: showOfficeMenu || showDefaultMenu,
    paymentInformation: showOfficeMenu || showDefaultMenu,
    paymentHistory: true,
    invoices: user.is_pre_approved,
    notificationSettings: showOversightMenu || showDefaultMenu,
    workerTypeManagement:
      (showOversightMenu || showDefaultMenu) &&
      childOffices.length > 0 &&
      shouldBeDisplayedForParent,
    workerTypeSettings: (showOfficeMenu || showDefaultMenu) && isWorkerClassificationEnabled,
    sendFeedback: showOfficeMenu || showDefaultMenu,
    helpCenter: true,
    termsOfService: true,
    about: true,
    referralLink: Boolean((showOfficeMenu || showDefaultMenu) && showReferralLinkMenu),
    bestInDental: isBestInDentalVisible,
    logOut: true,
  };

  useEffect(() => {
    dispatch(fetchNotificationSettings());
  }, [dispatch]);

  if (isLoading) {
    return (
      <Screen variant="standard">
        <Screen.Header title="Account" />
        <Screen.Body>
          <CenteredSpinner minHeight={400} />
        </Screen.Body>
      </Screen>
    );
  }

  return (
    <>
      <Screen variant="standard">
        <Screen.Header title="Account" />
        <Screen.Body>
          <MenuWrapper show={menuVisibility.profileHeaderCard}>
            <Row gap={32} alignItems="center">
              <ProfileHeaderCard />
              <BestInDentalWinner />
            </Row>
          </MenuWrapper>

          <Column pt={8} pl={5} gap={8}>
            <MenuWrapper show={menuVisibility.accountInformation}>
              <AccountMenuItem
                dataCy="account-information"
                text="Account Information"
                to="/account/information"
                endAdornment={
                  !isAccountInformationValid && (
                    <SolidExclamationWarningAlertIcon color={colors.warning[500]} />
                  )
                }
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.paymentInformation}>
              <AccountMenuItem
                dataCy="payment-information"
                text="Payment Information"
                to="/account/payment"
                endAdornment={
                  (isPaymentExpired || isPaymentExpiring) && (
                    <SolidExclamationWarningAlertIcon
                      color={
                        !isPaymentExpired && isPaymentExpiring
                          ? colors.warning[500]
                          : colors.error[500]
                      }
                    />
                  )
                }
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.paymentHistory}>
              <AccountMenuItem
                dataCy="payment-history"
                text="Payment History"
                to="/dashboard/receipts"
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.invoices}>
              <AccountMenuItem dataCy="invoices" text="Invoices" to="/account/invoices" />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.notificationSettings}>
              <AccountMenuItem
                dataCy="notification-settings"
                text="Notification Settings"
                to="/dashboard/notifications"
                endAdornment={
                  showNotificationWarning && (
                    <SolidExclamationWarningAlertIcon color={colors.error[500]} />
                  )
                }
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.workerTypeSettings}>
              <AccountMenuItem
                dataCy="worker-type"
                text="Worker Type"
                to={routes.workerClassificationType}
                endAdornment={
                  !user?.workerClassification?.classificationType && (
                    <SolidExclamationWarningAlertIcon color={colors.error[500]} />
                  )
                }
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.workerTypeManagement}>
              <AccountMenuItem
                dataCy="worker-type-management"
                text="Worker Type Management"
                to="/account/worker-classification-management"
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.sendFeedback}>
              <AccountMenuItem
                dataCy="send-feedback"
                text="Send Feedback"
                onClick={openPlatformFeedbackSurvey}
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.bestInDental}>
              <AccountMenuItem
                dataCy="best-in-dental"
                text="Best in Dental"
                to="/dashboard/best-in-dental"
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.helpCenter}>
              <AccountMenuItem
                dataCy="help-center"
                text="Help Center"
                to={routes.help}
                onClick={fireMenuBarHelpClicked}
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.termsOfService}>
              <AccountMenuItem
                dataCy="terms-of-service"
                text="Terms of Service"
                externalHref={Links.termsOfUse}
              />
            </MenuWrapper>

            <MenuWrapper show={menuVisibility.logOut}>
              <AccountMenuItem
                dataCy="logout"
                text="Log Out"
                onClick={() => {
                  trackEvent(POSTHOG_EVENTS.OFFICE_LOGOUT_CLICKED);
                  setShowLogoutPopup(true);
                }}
              />
            </MenuWrapper>
          </Column>
        </Screen.Body>
      </Screen>

      {showLogoutPopup && (
        <ConfirmPopup
          title="Confirm Log Out"
          description="Are you sure you would like to log out?"
          leftButtonText="Go Back"
          leftButtonAction={() => setShowLogoutPopup(false)}
          rightButtonText="Log Out"
          rightButtonAction={() => {
            trackEvent(POSTHOG_EVENTS.OFFICE_LOGOUT_CONFIRMED);
            sessionStorage.clear();
            dispatch(logOut());
            setShowLogoutPopup(false);
          }}
          dataCy="logout-confirmation-modal"
          rightButtonDataCy="btn-confirm-logout"
          leftButtonDataCy="btn-cancel-logout"
        />
      )}

      {showLinkReferralAccountPopup && (
        <LinkReferralPopup
          onClosePopup={() => {
            dispatch({ type: HIDE_LINK_REFERRAL_ACCOUNT_POPUP });
          }}
          onLinkAccount={(referralLink) => {
            dispatch(linkReferralAccount(referralLink));
          }}
          errorMessage={linkReferralError}
          isLoading={isLinkingReferral}
        />
      )}
    </>
  );
}

const MenuWrapper = ({ children, show = true }) => {
  if (!show) {
    return null;
  }
  return children;
};
